import * as React from 'react';
import MultiClamp from 'react-multi-clamp';
import styled from 'styled-components';

import Text from 'crehana-ui/Text';
import Avatar from 'crehana-ui/Avatar';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import Star from 'crehana-icons/Star';

import { useTranslation } from 'Shared/Hooks/useTranslation';
import getImgxUrl from 'Jsx/Utils/getImgxUrl';
import EVENTS from 'Jsx/Shared/Tracking/events';

const CoursePreviewVideoContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.gray.dark};
  width: 100%;
  height: 0;
  padding-bottom: 56.25%;
  position: relative;
`;

const CoursePreviewContent = ({ data, wistiaLoad }) => {
  const defaultPlayer = React.useRef({});
  const { score, user, prices } = data;
  const isPremium = user && user.isPremiumMember;
  const isFree = prices && prices.price === 0;
  const hideCTA =
    isPremium ||
    isFree ||
    data.courseType === 'membership' ||
    data.isEnrollment ||
    data.customBundle ||
    data.hidePreviewCTA;
  const { t } = useTranslation('main-menu');

  const buyCourse = (e, nextUrl) => {
    e.preventDefault();
    window.track(EVENTS.COURSE_CARD.BUY_NOW_PREVIEW, {
      url: window.location.href,
      currentPage: data.courseCardPage,
    });
    setTimeout(() => {
      window.location.href = nextUrl;
    }, 0);
  };

  const setDefaultPlayer = () => {
    try {
      window._wq = window._wq || [];

      window._wq.push({
        id: data.promoVideoHashedId,
        onReady: player => {
          defaultPlayer.current = player;
          defaultPlayer.current.play();
        },
      });
    } catch (error) {
      console.warn(
        'CampaignLanding/CoursePreviewContent: Error to play the video',
      );
    }
  };

  React.useEffect(() => {
    setDefaultPlayer();
  }, [wistiaLoad, data.promoVideoHashedId]);

  return (
    <div>
      <div className="flex items-center">
        <Text className="mr-16" type="caption" color="gray-dark">
          Valoración
        </Text>
        <Star size={12} color="#F7DB5C" />
        <Text type="caption" color="gray-dark" className="ml-8">
          {`${score && score.average && score.average > 0 ? score.average : 5}`}
        </Text>
      </div>
      <a href={data.url} target="_blank" rel="noopener noreferrer">
        <Text
          type="subtitle2"
          className="flex mt-8 mb-12 hover:text-purple-main"
        >
          <MultiClamp ellipsis="..." clamp={2} disableCssClamp>
            {data.title}
          </MultiClamp>
        </Text>
      </a>
      {data.professor && (
        <div className="flex">
          <Avatar
            baseUrl=""
            src={getImgxUrl(data.professor.profilePictureUrl, {
              w: 36,
              h: 36,
              crop: 'faces',
            })}
            size={36}
          />
          <div className="flex flex-col ml-8">
            <Text type="caption" color="gray-dark">
              {data.professor.fullName}
            </Text>
            <Text type="legals" color="gray-dark">
              <MultiClamp ellipsis="..." clamp={1} disableCssClamp>
                {data.professor.occupation}
              </MultiClamp>
            </Text>
          </div>
        </div>
      )}
      {data.promoVideoHashedId ? (
        <div className="mt-16" style={{ minHeight: '160px' }}>
          <CoursePreviewVideoContainer>
            <div
              id={`my-video-preview-${data.originalId}`}
              className={`wistia_embed wistia_async_${data.promoVideoHashedId} videoFoam=true inset-0 volume=0 absolute w-full h-full`}
            />
          </CoursePreviewVideoContainer>
        </div>
      ) : null}
      <Text type="label" color="gray-dark" className="flex mt-16">
        {t('COURSE_PREVIEW_DESCRIPTION', {
          defaultValue: 'Qué aprenderás',
        })}
      </Text>
      {data.description && (
        <div className="mt-8">
          <Text type="body4">
            <MultiClamp ellipsis="..." clamp={4} disableCssClamp>
              {data.description}
            </MultiClamp>
          </Text>
        </div>
      )}
      {hideCTA ? null : (
        <PrimaryButton
          className="mt-24"
          full
          href={data.urlToBuyNow}
          onClick={e => buyCourse(e, data.urlToBuyNow)}
          label={t('CART_MENU_BUY_NOW_BUTTON', {
            defaultValue: 'Comprar ahora',
          })}
        />
      )}
    </div>
  );
};

export default CoursePreviewContent;
